"use client";

import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isTokenExpired } from "@/lib/socket-auth";

export type CallbackEvento = {
  id: string;
  status: string;
  motivoFalha: string | null;
  conversationId: number | null;
  clienteNome: string;
  ownerUserId: string;
  executadoPorUserId: string | null;
};

// Motivos de "NAO ENVIADO" — o template nunca saiu. SEM_RESPOSTA e
// FALHA_ENTREGA sao tratados a parte no handleDisparado: sao os dois
// desfechos em que o template SAIU (a falha e do cliente nao responder, ou
// da operadora aceitar o envio e recusar a entrega DEPOIS), entao o texto
// generico "nao saiu" nao pode vir daqui (M1). FALHA_ENTREGA continua neste
// mapa porque o texto dele e o complemento da frase do toast proprio (ver
// handleDisparado abaixo); o popover tem o seu proprio mapa
// (`MOTIVO_FALHA_TEXTO` em use-agenda.ts), com frases em caixa alta e
// pontuacao de rotulo.
const MOTIVO_TEXTO: Record<string, string> = {
  ATRASO: "venceu fora do horario tolerado",
  TEMPLATE_INVALIDO: "o template nao esta mais aprovado",
  FALHA_ENVIO: "nao foi possivel enviar",
  // Sem travessao aqui: a frase do toast ja abre com um ("Retorno para X nao
  // chegou — …"), e dois travessoes na mesma linha viravam salada de
  // pontuacao (review final).
  FALHA_ENTREGA: "a operadora recusou a entrega, o cliente nao recebeu",
};

// Registro de assinantes em escopo de MODULO — nao de componente. Existe uma
// unica conexao de socket (montada em dashboard/layout.tsx via
// useAgendaSocket()); qualquer outra tela que precise reagir aos mesmos
// eventos (ex.: /dashboard/agenda) assina aqui em vez de chamar
// useAgendaSocket() de novo, o que abriria uma 2a conexao (`io(...)` roda
// dentro do useEffect do hook) e duplicaria os toasts.
const assinantes = new Set<(e: CallbackEvento) => void>();

function notificarAssinantes(evento: CallbackEvento) {
  for (const cb of assinantes) cb(evento);
}

/**
 * Assina os eventos `callback.disparado`/`callback.concluido`/
 * `callback.cancelado` sem abrir uma nova conexao — reusa o socket unico que `useAgendaSocket()` (montado uma vez
 * no layout do dashboard) ja mantem vivo. Use isto em qualquer tela que
 * precise reagir a esses eventos (ex.: aplicar in-place na grade da agenda).
 */
export function useAgendaEventos(onEvento: (evento: CallbackEvento) => void): void {
  const onEventoRef = useRef(onEvento);
  onEventoRef.current = onEvento;

  useEffect(() => {
    const cb = (evento: CallbackEvento) => onEventoRef.current(evento);
    assinantes.add(cb);
    return () => {
      assinantes.delete(cb);
    };
  }, []);
}

interface UseAgendaSocketOptions {
  onEvento?: (evento: CallbackEvento) => void;
}

export function useAgendaSocket(opts?: UseAgendaSocketOptions) {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const router = useRouter();
  const onEventoRef = useRef(opts?.onEvento);
  onEventoRef.current = opts?.onEvento;

  useEffect(() => {
    const token = api.getAccessToken();
    if (!token) return;

    // Socket.IO conecta na raiz do dominio (o proxy manda /socket.io pra API).
    const apiUrl =
      process.env.NEXT_PUBLIC_API_URL ||
      (typeof window !== "undefined" ? `${window.location.origin}/api` : "/api");
    const wsUrl = apiUrl.replace("/api", "");
    const socket = io(`${wsUrl}/agenda`, {
      auth: { token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 3000,
    });

    const buildAction = (conversationId: number | null) =>
      conversationId
        ? {
            action: {
              label: "Abrir conversa",
              onClick: () => router.push(`/dashboard/chat/${conversationId}`),
            },
          }
        : undefined;

    const handleDisparado = (evento: CallbackEvento) => {
      if (evento.status === "NAO_ATENDIDO" && evento.motivoFalha === "SEM_RESPOSTA") {
        // O template SAIU — dizer "nao saiu" aqui seria o oposto do fato
        // (M1). A falha e o cliente nao ter respondido, nao o envio.
        toast.info(
          `Retorno enviado para ${evento.clienteNome} — ninguem respondeu a tempo`,
          buildAction(evento.conversationId),
        );
      } else if (evento.status === "NAO_ATENDIDO" && evento.motivoFalha === "FALHA_ENTREGA") {
        // Mesmo espirito do fix M1 acima: o template TAMBEM saiu aqui — a
        // operadora aceitou o envio e recusou a entrega DEPOIS. Prefixar com
        // "nao saiu" (branch generica abaixo) contradiria o fato. Aqui e so o
        // ESTADO do callback; a causa acionavel (ex.: pendencia de pagamento
        // na conta Meta) vem no toast do evento `chat:message:delivery_failed`,
        // consumido por `use-chat-socket.ts` — quem esta com a conversa aberta
        // no chat ve os dois, quem esta em outra tela ve so este.
        toast.info(
          `Retorno para ${evento.clienteNome} nao chegou — ${MOTIVO_TEXTO.FALHA_ENTREGA}`,
          buildAction(evento.conversationId),
        );
      } else if (evento.status === "NAO_ATENDIDO") {
        const motivoTexto = evento.motivoFalha ? MOTIVO_TEXTO[evento.motivoFalha] : undefined;
        toast.info(
          `Retorno para ${evento.clienteNome} nao saiu — ${motivoTexto ?? "falha"}`,
          buildAction(evento.conversationId),
        );
      } else {
        toast.info(
          `Retorno enviado para ${evento.clienteNome}`,
          buildAction(evento.conversationId),
        );
      }
      onEventoRef.current?.(evento);
      notificarAssinantes(evento);
    };

    const handleConcluido = (evento: CallbackEvento) => {
      toast.info(`Retorno para ${evento.clienteNome} concluido`);
      onEventoRef.current?.(evento);
      notificarAssinantes(evento);
    };

    // I1 do review final: o backend ja emitia `callback.cancelado`
    // (CallbackConclusaoService.cancelarPorRespostaDoCliente — cliente escreveu
    // ANTES do horario, o motor reabriu a conversa e o compromisso perdeu
    // sentido), mas ninguem assinava aqui. A grade aplica evento in-place e
    // nunca refetcha (regra 1 de chat-performance-rules.md), entao o bloco
    // continuava AGENDADO na tela: o atendente clicava "Reagendar" e levava 400
    // sem nunca ter sido avisado. So F5 corrigia.
    const handleCancelado = (evento: CallbackEvento) => {
      toast.info(
        `Retorno para ${evento.clienteNome} cancelado — o cliente respondeu antes do horario`,
        buildAction(evento.conversationId),
      );
      onEventoRef.current?.(evento);
      notificarAssinantes(evento);
    };

    // Token expirado: refresh + reconnect. Espelha use-presence-connection.ts
    // (paridade obrigatoria — os outros 2 hooks de socket do dashboard ja
    // tratam isso; sem isso o socket reconectava pra sempre com o token
    // velho, calado, e o atendente nunca mais recebia toast de callback
    // depois de uma sessao longa (C2 do review final).
    const refreshAttemptedRef = { current: false };
    const doRefreshAndReconnect = () => {
      if (refreshAttemptedRef.current) return;
      refreshAttemptedRef.current = true;
      api
        .refreshTokens()
        .then((ok) => {
          if (!ok) return; // refreshTokens ja redireciona /login se falhar
          const newToken = api.getAccessToken();
          if (!newToken) return;
          socket.auth = { token: newToken };
          socket.disconnect();
          socket.connect();
        })
        .catch(() => {});
    };

    socket.on("connect", () => {
      refreshAttemptedRef.current = false;
      setConnected(true);
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("auth_required", () => doRefreshAndReconnect());
    socket.on("connect_error", () => {
      if (isTokenExpired(api.getAccessToken())) doRefreshAndReconnect();
    });
    socket.on("callback.disparado", handleDisparado);
    socket.on("callback.concluido", handleConcluido);
    socket.on("callback.cancelado", handleCancelado);

    // Token rotacionado atualiza socket.auth do socket VIVO, pra a
    // reconexao organica (blip de rede, nao expiracao) nao subir com token
    // congelado.
    const unsubTokenRefresh = api.onTokenRefreshed((newToken) => {
      socket.auth = { token: newToken };
    });

    socketRef.current = socket;

    return () => {
      unsubTokenRefresh();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [router]);

  return { connected };
}
